import Icon from '@/components/snippets/icon/icon'
import { useFilters } from 'libs/searchspring/hooks/useFilters'
import { useSort } from 'libs/searchspring/hooks/useSort'
import { useState, useEffect } from 'preact/hooks'
import { h, render, VNode } from 'preact'

interface FilterToggleButtonProps {
  className?: string
  iconClass?: string
  standalone?: boolean
}

interface SortToggleButtonProps {
  className?: string
  iconClass?: string
  standalone?: boolean
}

function FilterToggleButton({
  className = '',
  iconClass = '',
  standalone = false
}: FilterToggleButtonProps): VNode<any> {
  const [activeFilterCount, setActiveFilterCount] = useState<number>(0)

  let filters = null
  if (!standalone) {
    try {
      filters = useFilters()
    } catch {}
  }

  useEffect(() => {
    if (filters) {
      const count = filters.activeFilters?.size || 0
      setActiveFilterCount(count)

      window.dispatchEvent(new CustomEvent('searchspring:filters:updated', {
        detail: { filterCount: count }
      }))
    } else {
      const updateFilterCount = () => {
        const urlParams = new URLSearchParams(window.location.search)
        const filtersParam = urlParams.get('filters')
        if (filtersParam && filtersParam.trim()) {
          const filterArray = filtersParam.split(',').filter(f => f.trim())
          setActiveFilterCount(filterArray.length)
        } else {
          setActiveFilterCount(0)
        }
      }

      updateFilterCount()
      window.addEventListener('popstate', updateFilterCount)

      const handleFilterUpdate = (e: CustomEvent) => {
        if (e.detail?.filterCount !== undefined) {
          setActiveFilterCount(e.detail.filterCount)
        }
      }
      window.addEventListener('searchspring:filters:updated', handleFilterUpdate as EventListener)

      return () => {
        window.removeEventListener('popstate', updateFilterCount)
        window.removeEventListener('searchspring:filters:updated', handleFilterUpdate as EventListener)
      }
    }
  }, [filters])

  const handleFilterOpen = () => {
    if (filters) {
      filters.handleOpenFilters()
    }

    window.dispatchEvent(new CustomEvent('searchspring:open-filters'))
    window.dispatchEvent(new CustomEvent('searchspring:close-sort-accordion'))
  }

  const buttonText = [
    activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Add Filters',
    h('span', { class: 'md:hidden' }, ' & Sort')
  ]

  return h('button', {
    className,
    onClick: handleFilterOpen,
    'aria-label': 'Toggle Filters'
  }, [
    iconClass ? h('div', { class: `${iconClass} children:size-full` }, h(Icon, { id: 'filter' })) : null,
    h('span', null, buttonText)
  ].filter(Boolean))
}

function SortToggleButton({
  className = '',
  iconClass = '',
  standalone = false
}: SortToggleButtonProps): VNode<any> {
  const [activeSortLabel, setActiveSortLabel] = useState<string>('Recommended')
  const [sortMappings, setSortMappings] = useState<Record<string, string>>({})

  let sortContext = null
  let filtersContext = null
  if (!standalone) {
    try {
      sortContext = useSort()
      filtersContext = useFilters()
    } catch {}
  }

  useEffect(() => {
    if (sortContext) {
      const { sorts, activeSort } = sortContext
      const label = activeSort ? sorts.find((sort: any) => sort.value === activeSort)?.label : 'Recommended'
      setActiveSortLabel(label || 'Recommended')

      const mappings: Record<string, string> = {}
      sorts.forEach((sort: any) => {
        mappings[sort.value] = sort.label
      })

      window.dispatchEvent(new CustomEvent('searchspring:sort:updated', {
        detail: {
          sortLabel: label || 'Recommended',
          sorts: mappings
        }
      }))
    } else {
      const updateSort = () => {
        const urlParams = new URLSearchParams(window.location.search)
        const sortParam = urlParams.get('sort')

        if (sortParam && sortMappings[sortParam]) {
          setActiveSortLabel(sortMappings[sortParam])
        } else if (sortParam) {
          setActiveSortLabel('Recommended')
        } else {
          setActiveSortLabel('Recommended')
        }
      }

      updateSort()
      window.addEventListener('popstate', updateSort)

      const handleSortUpdate = (e: CustomEvent) => {
        if (e.detail?.sortLabel) {
          setActiveSortLabel(e.detail.sortLabel)
        }
        if (e.detail?.sorts) {
          setSortMappings(e.detail.sorts)
        }
      }
      window.addEventListener('searchspring:sort:updated', handleSortUpdate as EventListener)

      return () => {
        window.removeEventListener('popstate', updateSort)
        window.removeEventListener('searchspring:sort:updated', handleSortUpdate as EventListener)
      }
    }
  }, [sortContext, sortMappings])

  const handleSortOpen = () => {
    if (filtersContext) {
      filtersContext.handleOpenFilters()
    }

    window.dispatchEvent(new CustomEvent('searchspring:open-filters'))
    window.dispatchEvent(new CustomEvent('searchspring:close-filter-accordions'))
    window.dispatchEvent(new CustomEvent('searchspring:open-sort-accordion'))
  }

  const buttonText = `Sort: ${activeSortLabel}`

  return h('button', {
    className,
    onClick: handleSortOpen,
    'aria-label': 'Toggle Sort By'
  }, [
    iconClass ? h('div', { class: `${iconClass} children:size-full` }, h(Icon, { id: 'sort' })) : null,
    h('span', null, buttonText)
  ].filter(Boolean))
}

export { FilterToggleButton, SortToggleButton }

export class FilterToggleButtonElement extends HTMLElement {
  connectedCallback(): void {
    render(
      h(FilterToggleButton, {
        className: this.getAttribute('data-class') || '',
        iconClass: this.getAttribute('data-icon-class') || '',
        standalone: true
      }),
      this
    )
  }
}

export class SortToggleButtonElement extends HTMLElement {
  connectedCallback(): void {
    render(
      h(SortToggleButton, {
        className: this.getAttribute('data-class') || '',
        iconClass: this.getAttribute('data-icon-class') || '',
        standalone: true
      }),
      this
    )
  }
}
